import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BillingSyncBadge } from "@/components/BillingSyncBadge";
import { InvoicePrintSheet, printInvoice } from "@/components/pharmacy/InvoicePrintSheet";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { formatInvoiceDateShort, formatMoney } from "@/core/pharmacy/format";
import {
  listValidatedInvoices,
  searchValidatedInvoices,
  syncPharmacyInvoicesFromApi,
} from "@/core/pharmacy/pharmacy-store";
import type { ValidatedInvoice } from "@/core/pharmacy/types";
import { routes } from "@/router/all_routes";

const DEFAULT_PAGE_SIZE = 50;

export function PharmacyInvoiceHistoryPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ValidatedInvoice | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void syncPharmacyInvoicesFromApi().finally(() => {
      if (!cancelled) setRefreshKey((k) => k + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const invoices = useMemo(() => {
    void refreshKey;
    if (query.trim()) return searchValidatedInvoices(query);
    return listValidatedInvoices();
  }, [query, refreshKey]);

  const filteredTotal = useMemo(
    () => invoices.reduce((sum, inv) => sum + inv.subtotal, 0),
    [invoices],
  );

  useEffect(() => {
    setPage(1);
  }, [query, pageSize]);

  const totalPages = Math.max(1, Math.ceil(invoices.length / pageSize));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedInvoices = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return invoices.slice(start, start + pageSize);
  }, [invoices, safePage, pageSize]);

  return (
    <div>
      <PageHeader
        title="Historique des factures"
        description="Recherchez par numéro, client, téléphone ou produit — imprimez une facture validée."
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(routes.tenantDashboard)}
            >
              Tableau de bord
            </button>
            <Link to={routes.pharmacyInvoice} className="btn btn-primary btn-sm">
              Nouvelle facture
            </Link>
          </div>
        }
      />

      <div className="ph-invoice-sheet mb-4">
        <label className="form-label small fw-semibold mb-2" htmlFor="inv-search">
          Rechercher
        </label>
        <div className="d-flex gap-2 flex-wrap">
          <input
            id="inv-search"
            type="search"
            className="form-control form-control-sm flex-grow-1"
            style={{ maxWidth: "28rem" }}
            placeholder="N° facture, nom client, téléphone, produit…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setQuery("")}
            >
              Effacer
            </button>
          )}
        </div>
        <p className="small text-muted mb-0 mt-2">
          {invoices.length} facture{invoices.length !== 1 ? "s" : ""} trouvée
          {invoices.length !== 1 ? "s" : ""}
          {invoices.length > 0 && (
            <span className="ms-1">· total {formatMoney(filteredTotal)}</span>
          )}
        </p>
      </div>

      <div className="ph-invoice-sheet">
        {invoices.length === 0 ? (
          <div className="text-center text-muted py-5">
            <p className="mb-2">Aucune facture ne correspond à votre recherche.</p>
            <Link to={routes.pharmacyInvoice} className="btn btn-sm btn-primary">
              Créer une facture
            </Link>
          </div>
        ) : (
          <>
            <div className="table-responsive ph-diag-table">
              <table className="table table-sm ph-dash-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Billing</th>
                    <th className="text-end">Montant</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedInvoices.map((invoice) => (
                  <tr key={invoice.id} className="ph-diag-row">
                    <td data-label="N°">
                      <span className="ph-inv-badge">{invoice.number}</span>
                    </td>
                    <td data-label="Date" className="small">
                      {formatInvoiceDateShort(invoice.validatedAt)}
                    </td>
                    <td data-label="Client">
                      <div className="fw-semibold">{invoice.customer.name}</div>
                      {invoice.customer.phone && (
                        <div className="small text-muted">{invoice.customer.phone}</div>
                      )}
                    </td>
                    <td data-label="Billing">
                      <BillingSyncBadge sync={invoice.billingSync} />
                    </td>
                    <td data-label="Montant" className="text-end fw-semibold">
                      {formatMoney(invoice.subtotal)}
                    </td>
                    <td data-label="Actions" className="text-end">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 me-2"
                        onClick={() => setSelected(invoice)}
                      >
                        Voir
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => printInvoice(invoice)}
                      >
                        Imprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            <TablePagination
              page={safePage}
              pageSize={pageSize}
              totalItems={invoices.length}
              pageSizeOptions={[25, 50, 100, 200]}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>

      {selected && (
        <div
          className="ph-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="inv-detail-title"
          onClick={() => setSelected(null)}
        >
          <div
            className="ph-modal-card ph-invoice-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h3 id="inv-detail-title" className="h5 mb-0">
                Facture {selected.number}
              </h3>
              <button
                type="button"
                className="btn-close"
                aria-label="Fermer"
                onClick={() => setSelected(null)}
              />
            </div>

            <InvoicePrintSheet invoice={selected} />

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setSelected(null)}
              >
                Fermer
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => printInvoice(selected)}
              >
                Imprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
